<script setup lang="ts">
import { UserIcon } from 'vue-tabler-icons';
import { formatRp } from '@/utils/format';

const router = useRouter();
const { t, locale } = useI18n();
const { mode: themeMode, setMode: setThemeMode, load } = useThemeMode();
const { me, refresh } = useAuthSession();
const { forceLogout, startWatchers, urgent } = useIdleSession();
const { getRealViewportWidth, CHANGE_EVENT, isFramed } = useDevicePreview();
const menuOpen = ref(false);

/** Dummy contoh saat user belum ditautkan / belum ada data gaji */
const DUMMY_GAJI = 8_500_000;
const DUMMY_KOMISI = 1_250_000;

type LastAtt = {
  date?: string | null;
  time?: string | null;
  checkIn?: string | null;
  checkOut?: string | null;
  status?: string | null;
};

const paySnap = ref<{
  gaji: number | null;
  komisi: number | null;
  demo: boolean;
  position: string | null;
  joinedAt: string | null;
  lastAttendance: LastAtt | null;
}>({
  gaji: null,
  komisi: null,
  demo: true,
  position: null,
  joinedAt: null,
  lastAttendance: null,
});

function formatDateId(iso: string | null | undefined) {
  const s = String(iso || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '—';
  const [y, m, d] = s.split('-');
  return `${d}/${m}/${y}`;
}

function lastAttendanceParts(att: LastAtt | null | undefined) {
  if (!att?.date) return { date: '—', time: '' };
  const date = formatDateId(att.date);
  const time = String(att.time || att.checkOut || att.checkIn || '').trim();
  return { date, time };
}

async function loadPaySnap() {
  try {
    const data = await $fetch<{
      gaji: number | null;
      komisi: number | null;
      demo?: boolean;
      linked?: boolean;
      position?: string | null;
      joinedAt?: string | null;
      lastAttendance?: LastAtt | null;
      view?: {
        meta?: {
          position?: string | null;
          joinedAt?: string | null;
          lastAttendance?: LastAtt | null;
        };
        employee?: { position?: string | null; joinedAt?: string | null };
      };
    }>('/api/account/profile');
    const meta = data.view?.meta;
    paySnap.value = {
      gaji: data.gaji ?? null,
      komisi: data.komisi ?? null,
      demo: !!data.demo || data.linked === false,
      position: data.position || meta?.position || data.view?.employee?.position || null,
      joinedAt: data.joinedAt || meta?.joinedAt || data.view?.employee?.joinedAt || null,
      lastAttendance: data.lastAttendance || meta?.lastAttendance || null,
    };
  } catch {
    paySnap.value = {
      gaji: null,
      komisi: null,
      demo: true,
      position: null,
      joinedAt: null,
      lastAttendance: null,
    };
  }
}

const gajiIsDemo = computed(() => paySnap.value.gaji == null);
const komisiIsDemo = computed(() => paySnap.value.komisi == null);
const payIsDemo = computed(() => gajiIsDemo.value || komisiIsDemo.value);
const gajiText = computed(() =>
  formatRp(gajiIsDemo.value ? DUMMY_GAJI : Number(paySnap.value.gaji))
);
const komisiText = computed(() =>
  formatRp(komisiIsDemo.value ? DUMMY_KOMISI : Number(paySnap.value.komisi))
);

const positionLabel = computed(
  () => paySnap.value.position || t('profile.positionDemo')
);
const joinedAtLabel = computed(() =>
  paySnap.value.joinedAt
    ? formatDateId(paySnap.value.joinedAt)
    : paySnap.value.demo
      ? formatDateId(`${new Date().getFullYear() - 1}-03-01`)
      : '—'
);
const lastAttParts = computed(() => {
  if (paySnap.value.lastAttendance?.date) {
    return lastAttendanceParts(paySnap.value.lastAttendance);
  }
  if (paySnap.value.demo) {
    return { date: '08/08/2026', time: '17:42' };
  }
  return { date: '—', time: '' };
});
onMounted(() => {
  load();
  collapseAll();
  void refresh().then(() => loadPaySnap());
  startWatchers();
  window.addEventListener(CHANGE_EVENT, onDevicePreviewChange);
  window.addEventListener('jgi-close-overlays', closePanel);
});
onUnmounted(() => {
  window.removeEventListener(CHANGE_EVENT, onDevicePreviewChange);
  window.removeEventListener('jgi-close-overlays', closePanel);
});

/** Tutup panel dulu — jangan unmount DeviceSwitch di tengah klik ganti mode. */
function onDevicePreviewChange() {
  menuOpen.value = false;
}

const displayLabel = computed(
  () => me.value.displayName || me.value.user || t('profile.myAccount')
);
const levelLabel = computed(() => me.value.levelName || me.value.levelCode || '—');
const initials = computed(() => {
  const n = displayLabel.value || '?';
  return n
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('');
});

const menuWidth = computed(() => {
  if (!import.meta.client) return 360;
  const frameW = getRealViewportWidth();
  return Math.min(360, Math.max(300, frameW - 24));
});

/** Framed → host di stage; jangan ke body (scrim full-viewport). */
const teleportTo = computed(() =>
  isFramed.value ? '#jgi-frame-overlays' : 'body'
);

const panelStyle = computed(() => {
  if (isFramed.value) return { width: '100%', maxWidth: '100%' };
  return { width: `${menuWidth.value}px`, maxWidth: 'min(360px, calc(100vw - 20px))' };
});

function logout() {
  void forceLogout('manual');
}

function goUsers() {
  menuOpen.value = false;
  router.push('/users');
}

function goAccount() {
  menuOpen.value = false;
  router.push('/account');
}

function closePanel() {
  menuOpen.value = false;
}

function togglePanel() {
  menuOpen.value = !menuOpen.value;
  if (menuOpen.value) {
    void loadPaySnap();
    // Selalu mulai tertutup — ringkas & konsisten desktop/HP
    collapseAll();
  }
}

type ProfileSec = 'work' | 'pay' | 'session' | 'prefs' | 'menu';
const openSecs = ref<Record<ProfileSec, boolean>>({
  work: false,
  pay: false,
  session: false,
  prefs: false,
  menu: false,
});

function toggleSec(id: ProfileSec) {
  openSecs.value = { ...openSecs.value, [id]: !openSecs.value[id] };
}

function expandAll() {
  openSecs.value = { work: true, pay: true, session: true, prefs: true, menu: true };
}

function collapseAll() {
  openSecs.value = { work: false, pay: false, session: false, prefs: false, menu: false };
}

const anyExpanded = computed(() => Object.values(openSecs.value).some(Boolean));
const foldLabel = computed(() =>
  anyExpanded.value ? t('profile.collapseAll') : t('profile.expandAll')
);
</script>

<template>
  <v-btn
    class="profileBtn custom-hover-primary"
    variant="text"
    rounded="circle"
    icon
    :aria-expanded="menuOpen"
    @click="togglePanel"
  >
    <v-avatar size="35" class="profile-dd__activator">
      <img src="/images/profile/user-1.jpg" height="35" alt="user" />
    </v-avatar>
  </v-btn>

  <Teleport v-if="menuOpen" :to="teleportTo">
    <div
      class="profile-sheet-root"
      :class="{
        'profile-sheet-root--sheet': isFramed,
        'profile-sheet-root--dropdown': !isFramed,
      }"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        class="profile-sheet-scrim"
        :aria-label="t('common.close')"
        @click="closePanel"
      />
      <div
        class="profile-dd"
        :class="{ 'profile-dd--sheet': isFramed }"
        :style="panelStyle"
      >
        <div class="profile-dd__topbar">
          <div v-if="isFramed" class="profile-dd__grab" aria-hidden="true">
            <span class="profile-dd__grab-pill" />
          </div>
          <div class="profile-dd__top-actions">
            <button
              type="button"
              class="profile-dd__fold-all"
              :title="foldLabel"
              :aria-label="foldLabel"
              @click="anyExpanded ? collapseAll() : expandAll()"
            >
              <v-icon size="16">
                {{ anyExpanded ? 'mdi-arrow-collapse-vertical' : 'mdi-arrow-expand-vertical' }}
              </v-icon>
            </button>
            <button
              type="button"
              class="profile-dd__close"
              :aria-label="t('common.close')"
              @click="closePanel"
            >
              <v-icon size="18">mdi-close</v-icon>
            </button>
          </div>
        </div>

        <header class="profile-dd__hero">
          <div class="profile-dd__hero-glow" aria-hidden="true" />
          <div class="profile-dd__hero-glow profile-dd__hero-glow--2" aria-hidden="true" />
          <div class="profile-dd__hero-row">
            <div class="profile-dd__avatar" aria-hidden="true">
              <span>{{ initials }}</span>
              <i class="profile-dd__avatar-ring" aria-hidden="true" />
            </div>
            <div class="profile-dd__hero-meta">
              <div class="profile-dd__kicker">{{ t('profile.myAccount') }}</div>
              <h3 class="profile-dd__name">{{ displayLabel }}</h3>
              <div class="profile-dd__idline">
                <span class="profile-dd__chip" :style="{ '--chip': me.levelColor || '#0f766e' }">
                  {{ levelLabel }}
                </span>
                <span v-if="me.user" class="profile-dd__handle">@{{ me.user }}</span>
              </div>
            </div>
          </div>
        </header>

        <div class="profile-dd__body">
          <section class="profile-dd__devices">
            <div class="profile-dd__devices-head">
              <v-icon size="14">mdi-devices</v-icon>
              <span>{{ t('device.label') }}</span>
            </div>
            <LayoutFullVerticalHeaderDeviceSwitch embedded />
          </section>

          <div class="profile-stack">
          <div class="profile-acc" :class="{ open: openSecs.work }">
            <button
              type="button"
              class="profile-acc__head"
              :aria-expanded="openSecs.work"
              @click="toggleSec('work')"
            >
              <span class="profile-acc__ico" aria-hidden="true">
                <v-icon size="15">mdi-briefcase-outline</v-icon>
              </span>
              <span class="profile-acc__title">{{ t('profile.secWork') }}</span>
              <span class="profile-acc__peek">{{ positionLabel }}</span>
              <span class="profile-acc__chev" aria-hidden="true">
                <v-icon size="16">mdi-chevron-down</v-icon>
              </span>
            </button>
            <v-expand-transition>
              <div v-show="openSecs.work" class="profile-acc__body">
                <div class="profile-dd__facts" :class="{ 'profile-dd__facts--demo': payIsDemo }">
                  <div class="fact-row">
                    <span class="fact-row__ico" aria-hidden="true">
                      <v-icon size="15">mdi-briefcase-outline</v-icon>
                    </span>
                    <span class="fact-row__label">{{ t('profile.position') }}</span>
                    <strong class="fact-row__value">{{ positionLabel }}</strong>
                  </div>
                  <div class="fact-row">
                    <span class="fact-row__ico" aria-hidden="true">
                      <v-icon size="15">mdi-calendar-start</v-icon>
                    </span>
                    <span class="fact-row__label">{{ t('profile.joinedAt') }}</span>
                    <strong class="fact-row__value">{{ joinedAtLabel }}</strong>
                  </div>
                  <div class="fact-row">
                    <span class="fact-row__ico fact-row__ico--att" aria-hidden="true">
                      <v-icon size="15">mdi-clock-check-outline</v-icon>
                    </span>
                    <span class="fact-row__label">{{ t('profile.lastAttendance') }}</span>
                    <strong class="fact-row__value fact-row__value--att">
                      <span>{{ lastAttParts.date }}</span>
                      <span v-if="lastAttParts.time" class="fact-row__time">{{ lastAttParts.time }}</span>
                    </strong>
                  </div>
                </div>
              </div>
            </v-expand-transition>
          </div>

          <div class="profile-acc" :class="{ open: openSecs.pay }">
            <button
              type="button"
              class="profile-acc__head"
              :aria-expanded="openSecs.pay"
              @click="toggleSec('pay')"
            >
              <span class="profile-acc__ico profile-acc__ico--pay" aria-hidden="true">
                <v-icon size="15">mdi-cash-multiple</v-icon>
              </span>
              <span class="profile-acc__title">{{ t('profile.payLabel') }}</span>
              <span class="profile-acc__peek">{{ gajiText }}</span>
              <span class="profile-acc__chev" aria-hidden="true">
                <v-icon size="16">mdi-chevron-down</v-icon>
              </span>
            </button>
            <v-expand-transition>
              <div v-show="openSecs.pay" class="profile-acc__body">
                <div
                  class="profile-dd__pay"
                  :class="{ 'profile-dd__pay--demo': payIsDemo }"
                  role="group"
                  :aria-label="t('profile.payLabel')"
                >
                  <div v-if="payIsDemo" class="pay-demo-badge">{{ t('profile.payDemo') }}</div>
                  <div class="pay-tile pay-tile--salary">
                    <span class="pay-tile__icon" aria-hidden="true">
                      <v-icon size="16">mdi-cash-multiple</v-icon>
                    </span>
                    <div class="pay-tile__meta">
                      <span class="pay-tile__label">{{ t('profile.salary') }}</span>
                      <strong class="pay-tile__value">{{ gajiText }}</strong>
                    </div>
                  </div>
                  <div class="pay-tile pay-tile--commission">
                    <span class="pay-tile__icon" aria-hidden="true">
                      <v-icon size="16">mdi-chart-timeline-variant</v-icon>
                    </span>
                    <div class="pay-tile__meta">
                      <span class="pay-tile__label">{{ t('profile.commission') }}</span>
                      <strong class="pay-tile__value">{{ komisiText }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </v-expand-transition>
          </div>

          <div class="profile-acc" :class="{ open: openSecs.session }">
            <button
              type="button"
              class="profile-acc__head"
              :aria-expanded="openSecs.session"
              @click="toggleSec('session')"
            >
              <span
                class="profile-acc__ico"
                :class="{ 'profile-acc__ico--urgent': urgent }"
                aria-hidden="true"
              >
                <v-icon size="15">mdi-timer-outline</v-icon>
              </span>
              <span class="profile-acc__title">{{ t('profile.secSession') }}</span>
              <span class="profile-acc__peek">{{ urgent ? t('profile.sessionWarn') : t('profile.sessionOn') }}</span>
              <span class="profile-acc__chev" aria-hidden="true">
                <v-icon size="16">mdi-chevron-down</v-icon>
              </span>
            </button>
            <v-expand-transition>
              <div v-show="openSecs.session" class="profile-acc__body">
                <div class="profile-dd__session" :class="{ 'profile-dd__session--urgent': urgent }">
                  <div class="profile-dd__session-icon" aria-hidden="true">
                    <v-icon size="16">mdi-timer-outline</v-icon>
                  </div>
                  <div class="profile-dd__session-body">
                    <LayoutFullVerticalHeaderSessionClock variant="profile" />
                  </div>
                </div>
              </div>
            </v-expand-transition>
          </div>

          <div class="profile-acc" :class="{ open: openSecs.prefs }">
            <button
              type="button"
              class="profile-acc__head"
              :aria-expanded="openSecs.prefs"
              @click="toggleSec('prefs')"
            >
              <span class="profile-acc__ico profile-acc__ico--prefs" aria-hidden="true">
                <v-icon size="15">mdi-tune-variant</v-icon>
              </span>
              <span class="profile-acc__title">{{ t('profile.secPrefs') }}</span>
              <span class="profile-acc__peek">{{ locale.toUpperCase() }} · {{ themeMode }}</span>
              <span class="profile-acc__chev" aria-hidden="true">
                <v-icon size="16">mdi-chevron-down</v-icon>
              </span>
            </button>
            <v-expand-transition>
              <div v-show="openSecs.prefs" class="profile-acc__body">
                <section class="profile-dd__section">
                  <div class="profile-dd__label">
                    <v-icon size="14">mdi-translate</v-icon>
                    <span>{{ t('profile.langLabel') }}</span>
                  </div>
                  <LayoutFullVerticalHeaderLangSwitch embedded />
                </section>

                <section class="profile-dd__section profile-dd__section--last">
                  <div class="profile-dd__label">
                    <v-icon size="14">mdi-palette-outline</v-icon>
                    <span>{{ t('theme.label') }}</span>
                  </div>
                  <div class="theme-switch" role="group" :aria-label="t('theme.label')">
                    <button
                      type="button"
                      class="theme-btn theme-btn--light"
                      :class="{ 'theme-btn--active': themeMode === 'light' }"
                      :aria-pressed="themeMode === 'light'"
                      @click="setThemeMode('light')"
                    >
                      <v-icon size="18">mdi-white-balance-sunny</v-icon>
                      <span>{{ t('theme.light') }}</span>
                    </button>
                    <button
                      type="button"
                      class="theme-btn theme-btn--dark"
                      :class="{ 'theme-btn--active': themeMode === 'dark' }"
                      :aria-pressed="themeMode === 'dark'"
                      @click="setThemeMode('dark')"
                    >
                      <v-icon size="18">mdi-moon-waning-crescent</v-icon>
                      <span>{{ t('theme.dark') }}</span>
                    </button>
                  </div>
                </section>
              </div>
            </v-expand-transition>
          </div>

          <div class="profile-acc" :class="{ open: openSecs.menu }">
            <button
              type="button"
              class="profile-acc__head"
              :aria-expanded="openSecs.menu"
              @click="toggleSec('menu')"
            >
              <span class="profile-acc__ico" aria-hidden="true">
                <v-icon size="15">mdi-menu</v-icon>
              </span>
              <span class="profile-acc__title">{{ t('profile.secMenu') }}</span>
              <span class="profile-acc__peek">{{ t('profile.menuPeek') }}</span>
              <span class="profile-acc__chev" aria-hidden="true">
                <v-icon size="16">mdi-chevron-down</v-icon>
              </span>
            </button>
            <v-expand-transition>
              <div v-show="openSecs.menu" class="profile-acc__body">
                <nav class="profile-dd__nav" :aria-label="t('profile.myAccount')">
                  <button v-if="me.canManageUsers" type="button" class="nav-row" @click="goUsers">
                    <span class="nav-row__icon">
                      <v-icon size="18">mdi-shield-account-outline</v-icon>
                    </span>
                    <span class="nav-row__label">{{ t('nav.users') }}</span>
                    <v-icon size="16" class="nav-row__chev">mdi-chevron-right</v-icon>
                  </button>
                  <button type="button" class="nav-row" @click="goAccount">
                    <span class="nav-row__icon"><UserIcon stroke-width="1.5" size="18" /></span>
                    <span class="nav-row__label">{{ t('profile.myAccount') }}</span>
                    <v-icon size="16" class="nav-row__chev">mdi-chevron-right</v-icon>
                  </button>
                </nav>
              </div>
            </v-expand-transition>
          </div>
          </div>

          <div class="profile-dd__fill" aria-hidden="true">
            <div class="profile-dd__fill-card">
              <span class="profile-dd__fill-ico">
                <v-icon size="18">mdi-shield-check-outline</v-icon>
              </span>
              <div class="profile-dd__fill-copy">
                <strong>{{ t('profile.fillTitle') }}</strong>
                <span>{{ t('profile.fillHint') }}</span>
              </div>
            </div>
          </div>
        </div>

        <footer class="profile-dd__foot">
          <button type="button" class="logout-btn" @click="logout">
            <v-icon size="18">mdi-logout</v-icon>
            {{ t('auth.logout') }}
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.profile-dd {
  --pd-ink: #0f172a;
  --pd-muted: #334155;
  --pd-line: #cbd5e1;
  --pd-surface: #ffffff;
  --pd-soft: #f1f5f9;
  --pd-soft2: #e2e8f0;
  --pd-accent: #0f766e;
  --pd-danger: #be123c;
  --pd-radius: 18px;
  position: relative;
  border-radius: var(--pd-radius);
  background: var(--pd-soft);
  border: 1px solid #94a3b8;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 24px 48px -16px rgba(15, 23, 42, 0.38),
    0 8px 20px -8px rgba(15, 23, 42, 0.18);
  overflow: hidden;
  max-height: min(88vh, 720px);
  display: flex;
  flex-direction: column;
  animation: pd-in 0.22s cubic-bezier(0.22, 1, 0.36, 1);
}
.profile-sheet-root {
  position: absolute;
  inset: 0;
  z-index: 2600;
  display: flex;
  flex-direction: column;
  pointer-events: auto;
}
.profile-sheet-root--dropdown {
  position: fixed;
  inset: 0;
  z-index: 2600;
  display: block;
  pointer-events: none;
}
.profile-sheet-root--dropdown .profile-sheet-scrim {
  pointer-events: auto;
  background: rgba(15, 23, 42, 0.42);
}
.profile-sheet-root--dropdown .profile-dd {
  pointer-events: auto;
  position: absolute;
  top: 56px;
  right: 12px;
  z-index: 1;
  max-height: min(88vh, 720px);
}
.profile-sheet-scrim {
  position: absolute;
  inset: 0;
  border: 0;
  padding: 0;
  background: rgba(15, 23, 42, 0.45);
  cursor: pointer;
}
.profile-sheet-root--sheet > .profile-dd--sheet,
.profile-sheet-root > .profile-dd--sheet {
  position: relative;
  z-index: 1;
  margin: 0;
  margin-top: 0;
  flex: 1 1 auto !important;
  align-self: stretch;
  width: 100%;
  height: 100% !important;
  max-height: 100% !important;
  min-height: 0 !important;
  border-radius: 20px 20px 0 0 !important;
  overflow: hidden;
}
.profile-dd--sheet {
  width: 100% !important;
  max-width: 100% !important;
  height: 100% !important;
  max-height: 100% !important;
  min-height: 0 !important;
  margin: 0 !important;
  border-radius: 20px 20px 0 0 !important;
  border: 0;
  box-shadow: 0 -12px 40px -12px rgba(15, 23, 42, 0.32);
  animation: pd-sheet-in 0.28s cubic-bezier(0.22, 1, 0.36, 1);
  background: #f1f5f9;
}
@keyframes pd-sheet-in {
  from {
    opacity: 0.6;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
.profile-dd__close {
  width: 30px;
  height: 30px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 9px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.profile-dd__close:hover {
  background: rgba(255, 255, 255, 0.28);
  border-color: rgba(255, 255, 255, 0.55);
}
.profile-dd__fold-all {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  font-size: 11px;
  font-weight: 750;
  cursor: pointer;
  border-radius: 9px;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.profile-dd__fold-all:hover {
  background: rgba(255, 255, 255, 0.28);
  border-color: rgba(255, 255, 255, 0.55);
}
.profile-dd__fold-all-txt {
  display: none;
}
.profile-dd__grab {
  display: none;
}
.profile-dd__topbar {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;
  gap: 6px;
  padding: 8px 12px 4px;
  border-bottom: 0;
  position: relative;
  z-index: 5;
  background: linear-gradient(180deg, #0f766e 0%, #115e59 100%);
}
.profile-dd__top-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}
.profile-dd--sheet .profile-dd__topbar {
  flex-direction: column;
  align-items: stretch;
  gap: 2px;
  padding: 8px 12px 6px;
  background: linear-gradient(180deg, #0f766e 0%, #115e59 100%);
  border-bottom: 0;
}
.profile-dd--sheet .profile-dd__grab {
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  padding: 0 0 2px;
  min-height: 0;
  background: transparent;
}
.profile-dd__grab-pill {
  width: 36px;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.55);
}
.profile-dd--sheet .profile-dd__top-actions {
  align-self: flex-end;
  z-index: 1;
}
.profile-dd--sheet .profile-dd__hero {
  padding: 4px 14px 14px;
  background: linear-gradient(180deg, #115e59 0%, #0f766e 100%);
  border-bottom: 0;
}
.profile-dd--sheet .profile-dd__hero-row {
  padding: 12px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.28);
  box-shadow: 0 8px 20px -12px rgba(15, 23, 42, 0.35);
  backdrop-filter: none;
}
.profile-dd--sheet .profile-dd__hero-glow {
  width: 180px;
  height: 180px;
  right: -50px;
  top: -70px;
  opacity: 0.9;
}
.profile-dd--sheet .profile-dd__hero-glow--2 {
  left: -60px;
  right: auto;
  top: 20px;
  width: 160px;
  height: 160px;
  background: radial-gradient(circle, rgba(14, 165, 233, 0.18), transparent 68%);
}
.profile-dd--sheet .profile-dd__avatar {
  width: 48px;
  height: 48px;
  border-radius: 16px;
  font-size: 16px;
  position: relative;
}
.profile-dd--sheet .profile-dd__avatar-ring {
  position: absolute;
  inset: -3px;
  border-radius: 18px;
  border: 1.5px solid rgba(15, 118, 110, 0.28);
  pointer-events: none;
}
.profile-dd--sheet .profile-dd__top-actions .profile-dd__close,
.profile-dd--sheet .profile-dd__top-actions .profile-dd__fold-all {
  position: static;
  top: auto;
  right: auto;
  width: 30px;
  height: 30px;
  padding: 0;
  margin: 0;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #fff;
  box-shadow: none;
  backdrop-filter: none;
}
.profile-dd--sheet .profile-dd__top-actions .profile-dd__fold-all {
  color: #fff;
}
.profile-dd__hero,
.profile-dd__foot,
.profile-dd__grab,
.profile-dd__topbar {
  flex-grow: 0 !important;
  flex-shrink: 0;
}
.profile-dd--sheet .profile-dd__body {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  max-height: none;
  overflow-x: hidden;
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 0 14px 12px;
  background: transparent;
  display: flex;
  flex-direction: column;
  gap: 10px;
  touch-action: pan-y;
}
.profile-dd--sheet .profile-dd__fold-all-txt {
  display: none !important;
}
/* Kartu konten premium */
.profile-dd--sheet .profile-stack {
  margin: 0;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  overflow: visible;
  backdrop-filter: none;
}
.profile-dd--sheet .profile-acc {
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}
.profile-dd--sheet .profile-acc:last-child {
  border-bottom: 1px solid #cbd5e1;
}
.profile-dd--sheet .profile-acc.open {
  background: #f0fdfa;
  border-color: #0f766e;
}
.profile-dd--sheet .profile-stack--body {
  margin: 0;
  border-top: 0;
}
.profile-dd--sheet .profile-acc__head {
  padding: 12px 14px;
  min-height: 48px;
  column-gap: 10px;
  grid-template-columns: 34px minmax(0, 1fr) minmax(0, max-content) 28px;
}
.profile-dd--sheet .profile-acc__head:hover {
  background: rgba(15, 23, 42, 0.025);
}
.profile-dd--sheet .profile-acc__ico {
  width: 34px;
  height: 34px;
  border-radius: 11px;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.55) inset;
}
.profile-dd--sheet .profile-acc__title {
  font-size: 13.5px;
  font-weight: 750;
  letter-spacing: -0.01em;
}
.profile-dd--sheet .profile-acc__peek {
  font-size: 11px;
  font-weight: 800;
  color: #115e59;
  background: #ccfbf1;
  border: 1px solid #5eead4;
  padding: 3px 9px;
  border-radius: 999px;
  max-width: 100%;
}
.profile-dd--sheet .profile-acc__peek--empty {
  background: transparent;
  border: 0;
  padding: 0;
}
.profile-dd--sheet .profile-acc__body {
  padding: 0 12px 12px;
}
.profile-dd--sheet .profile-dd__fill {
  flex: 0 0 auto;
  min-height: 0;
  margin-top: auto;
  display: flex;
  align-items: center;
  padding: 6px 0 2px;
}
.profile-dd--sheet .profile-dd__fill-card {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 14px;
  border-radius: 16px;
  border: 1px solid rgba(15, 118, 110, 0.22);
  background:
    linear-gradient(135deg, rgba(204, 251, 241, 0.95) 0%, rgba(224, 242, 254, 0.92) 55%, rgba(255, 255, 255, 0.88) 100%);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.7) inset,
    0 14px 28px -18px rgba(15, 118, 110, 0.45);
}
.profile-dd--sheet .profile-dd__fill-ico {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  color: #0f766e;
  background: rgba(15, 118, 110, 0.12);
  flex-shrink: 0;
}
.profile-dd--sheet .profile-dd__fill-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.profile-dd--sheet .profile-dd__fill-copy strong {
  font-size: 12.5px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.01em;
}
.profile-dd--sheet .profile-dd__fill-copy span {
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
}
.profile-dd--sheet .profile-dd__foot {
  margin-top: 0;
  flex: 0 0 auto !important;
  padding: 12px 14px calc(14px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.72), #fff 40%);
  backdrop-filter: blur(10px);
}
.profile-dd--sheet .logout-btn {
  min-height: 46px;
  border-radius: 14px;
  font-size: 13.5px;
  letter-spacing: 0.01em;
  background: #be123c;
  color: #fff;
  border-color: #9f1239;
  box-shadow: 0 8px 18px -10px rgba(190, 18, 60, 0.55);
}
@keyframes pd-in {
  from {
    opacity: 0;
    transform: translateY(-6px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.profile-dd__hero {
  position: relative;
  padding: 4px 14px 14px;
  background: linear-gradient(180deg, #115e59 0%, #0f766e 100%);
  border-bottom: 0;
  flex: 0 0 auto;
}
.profile-dd__hero-glow {
  position: absolute;
  width: 140px;
  height: 140px;
  right: -40px;
  top: -50px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(15, 118, 110, 0.22), transparent 68%);
  pointer-events: none;
}
.profile-dd__hero-glow--2 {
  display: none;
}
.profile-dd--sheet .profile-dd__hero-glow--2 {
  display: block;
}
.profile-dd__avatar-ring {
  display: none;
}
.profile-dd--sheet .profile-dd__avatar-ring {
  display: block;
}
.profile-dd:not(.profile-dd--sheet) .profile-dd__fill {
  display: none;
}
.profile-dd__hero-row {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding-right: 0;
}
.profile-dd__avatar {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: #fff;
  background: linear-gradient(145deg, #0f766e, #115e59 55%, #1e3a8a);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.25) inset,
    0 8px 14px -10px rgba(15, 118, 110, 0.65);
  flex-shrink: 0;
  position: relative;
}
.profile-dd__kicker {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #ccfbf1;
  margin-bottom: 2px;
}
.profile-dd__name {
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #fff;
  line-height: 1.2;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.profile-dd__idline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}
.profile-dd__chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  color: #fff;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.4);
}
.profile-dd__handle {
  font-size: 12px;
  font-weight: 700;
  color: #ccfbf1;
}
.profile-dd__hero-meta {
  min-width: 0;
  flex: 1;
  padding-right: 4px;
}
.profile-stack {
  position: relative;
  z-index: 1;
  margin-top: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-radius: 0;
  background: transparent;
  border: 0;
  overflow: visible;
  box-shadow: none;
  flex: 0 0 auto;
}
.profile-acc {
  position: relative;
  z-index: 1;
  margin-top: 0;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #cbd5e1;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  transition: background 0.15s ease, border-color 0.15s ease;
}
.profile-acc:last-child {
  border-bottom: 1px solid #cbd5e1;
}
.profile-acc.open {
  border-color: #0f766e;
  box-shadow: 0 0 0 1px rgba(15, 118, 110, 0.2);
  background: #f0fdfa;
}
.profile-acc--body {
  margin-top: 0;
  margin-bottom: 0;
  background: transparent;
  border-color: transparent;
}
.profile-acc__head {
  width: 100%;
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) minmax(0, max-content) 26px;
  align-items: center;
  column-gap: 10px;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 10px 12px;
  text-align: left;
  color: var(--pd-ink);
  min-height: 44px;
  box-sizing: border-box;
}
.profile-acc__head:hover {
  background: rgba(15, 23, 42, 0.025);
}
.profile-acc__ico {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  color: #0f766e;
  background: #ccfbf1;
  border: 1px solid #99f6e4;
}
.profile-acc__ico--pay {
  color: #92400e;
  background: #fef3c7;
  border-color: #fcd34d;
}
.profile-acc__ico--prefs {
  color: #075985;
  background: #e0f2fe;
  border-color: #7dd3fc;
}
.profile-acc__ico--urgent {
  color: #c2410c;
  background: rgba(234, 88, 12, 0.12);
}
.profile-acc__title {
  font-size: 13px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #0f172a;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.profile-acc__peek {
  justify-self: end;
  font-size: 11px;
  font-weight: 800;
  color: #115e59;
  background: #ccfbf1;
  border: 1px solid #5eead4;
  padding: 3px 8px;
  border-radius: 999px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 1;
  transition: opacity 0.15s ease;
}
.profile-acc__peek--empty {
  width: 0;
  min-width: 0;
  max-width: 0;
  padding: 0;
  margin: 0;
  border: 0;
  overflow: hidden;
  visibility: hidden;
  pointer-events: none;
}
.profile-acc.open .profile-acc__peek:not(.profile-acc__peek--empty) {
  opacity: 0;
  max-width: 0;
  padding-inline: 0;
  border-color: transparent;
  pointer-events: none;
}
.profile-acc__chev {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  justify-self: end;
  color: #64748b;
  background: rgba(15, 23, 42, 0.05);
  border: 1px solid rgba(15, 23, 42, 0.08);
  transition:
    transform 0.22s cubic-bezier(0.22, 1, 0.36, 1),
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
}
.profile-acc__head:hover .profile-acc__chev {
  background: rgba(15, 23, 42, 0.08);
  color: #334155;
}
.profile-acc.open .profile-acc__chev {
  transform: rotate(180deg);
  color: #0f766e;
  background: rgba(15, 118, 110, 0.12);
  border-color: rgba(15, 118, 110, 0.22);
}
.profile-dd--sheet .profile-acc__chev {
  width: 28px;
  height: 28px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.7) inset;
}
.profile-dd--sheet .profile-acc.open .profile-acc__chev {
  background: rgba(240, 253, 250, 0.98);
}
.profile-dd__devices {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid #cbd5e1;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}
.profile-dd__devices-head {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #334155;
}
.profile-acc__body {
  padding: 0 12px 10px;
}
.profile-dd__section--last {
  margin-bottom: 0;
}
.profile-dd__facts {
  position: relative;
  z-index: 1;
  margin-top: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  border-radius: 12px;
  background: rgba(248, 250, 252, 0.9);
  border: 1px solid rgba(15, 23, 42, 0.06);
  overflow: hidden;
}
.fact-row {
  display: grid;
  grid-template-columns: 26px minmax(80px, 34%) minmax(0, 1fr);
  align-items: center;
  gap: 6px;
  padding: 7px 9px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
.fact-row:last-child {
  border-bottom: 0;
}
.fact-row__ico {
  width: 28px;
  height: 28px;
  border-radius: 9px;
  display: grid;
  place-items: center;
  color: #0f766e;
  background: rgba(15, 118, 110, 0.12);
}
.fact-row__ico--att {
  color: #0369a1;
  background: rgba(14, 165, 233, 0.14);
}
.fact-row__label {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #475569;
  line-height: 1.25;
}
.fact-row__value {
  justify-self: end;
  text-align: right;
  font-size: 12px;
  font-weight: 800;
  color: var(--pd-ink);
  letter-spacing: -0.01em;
  line-height: 1.3;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.fact-row__value--att {
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: baseline;
  gap: 4px 6px;
}
.fact-row__time {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  color: #0369a1;
  background: rgba(14, 165, 233, 0.12);
  border: 1px solid rgba(14, 165, 233, 0.22);
  white-space: nowrap;
}
.profile-dd__facts--demo .fact-row__value {
  color: #475569;
}
.profile-dd__pay {
  position: relative;
  z-index: 1;
  margin-top: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding-top: 2px;
}
.pay-tile {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 10px 11px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(15, 23, 42, 0.07);
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.85) inset;
  backdrop-filter: blur(8px);
}
.pay-tile__icon {
  width: 28px;
  height: 28px;
  border-radius: 9px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
}
.pay-tile--salary .pay-tile__icon {
  color: #0f766e;
  background: rgba(15, 118, 110, 0.12);
}
.pay-tile--commission .pay-tile__icon {
  color: #b45309;
  background: rgba(245, 158, 11, 0.14);
}
.pay-tile__meta {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.pay-tile__label {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--pd-muted);
  line-height: 1.2;
}
.pay-tile__value {
  font-size: 12.5px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--pd-ink);
  line-height: 1.3;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.pay-demo-badge {
  position: absolute;
  top: -2px;
  right: 0;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #0f766e;
  background: rgba(236, 253, 245, 0.98);
  border: 1px solid rgba(15, 118, 110, 0.22);
  z-index: 2;
}
.profile-dd__pay--demo .pay-tile__value {
  color: #475569;
}

.profile-dd__session {
  position: relative;
  z-index: 1;
  margin-top: 0;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(248, 250, 252, 0.9);
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: none;
  backdrop-filter: none;
}
.profile-dd__session-body {
  min-width: 0;
  flex: 1;
  overflow-wrap: anywhere;
}
.profile-dd__session-icon {
  width: 28px;
  height: 28px;
  border-radius: 9px;
  display: grid;
  place-items: center;
  color: var(--pd-accent);
  background: rgba(15, 118, 110, 0.1);
  flex-shrink: 0;
  margin-top: 1px;
}
.profile-dd__session--urgent {
  border-color: rgba(234, 88, 12, 0.28);
  background: rgba(255, 247, 237, 0.9);
}
.profile-dd__session--urgent .profile-dd__session-icon {
  color: #c2410c;
  background: rgba(234, 88, 12, 0.12);
}

.profile-dd__body {
  padding: 10px 12px 4px;
  overflow-y: auto;
  overscroll-behavior: contain;
  /* Dropdown: tinggi mengikuti konten (jangan collapse ke 0). Sheet override di bawah. */
  flex: 0 1 auto;
  min-height: min-content;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.profile-dd:not(.profile-dd--sheet) .profile-dd__body {
  max-height: min(56vh, 440px);
}
.profile-dd__section {
  margin-bottom: 8px;
}
.profile-dd__label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #334155;
  margin-bottom: 5px;
}

.theme-switch {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 6px;
  padding: 4px;
  border-radius: 14px;
  background: var(--pd-soft2);
  border: 1px solid var(--pd-line);
  width: 100%;
  box-sizing: border-box;
}
.theme-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: none;
  cursor: pointer;
  padding: 7px 6px;
  border-radius: 9px;
  font-size: 11.5px;
  font-weight: 750;
  background: transparent;
  color: var(--pd-muted);
  min-width: 0;
  min-height: 34px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  white-space: nowrap;
  transition:
    background 0.18s ease,
    color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.15s ease;
}
.theme-btn:hover:not(.theme-btn--active) {
  color: var(--pd-ink);
  background: rgba(255, 255, 255, 0.7);
}
.theme-btn--light.theme-btn--active {
  color: #92400e;
  background: linear-gradient(180deg, #fffbeb 0%, #fef3c7 100%);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.85) inset,
    0 6px 14px -8px rgba(245, 158, 11, 0.55);
}
.theme-btn--light.theme-btn--active :deep(.v-icon) {
  color: #f59e0b;
}
.theme-btn--dark.theme-btn--active {
  color: #e0e7ff;
  background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
  box-shadow:
    0 1px 0 rgba(148, 163, 184, 0.15) inset,
    0 6px 14px -8px rgba(99, 102, 241, 0.5);
}
.theme-btn--dark.theme-btn--active :deep(.v-icon) {
  color: #a5b4fc;
}

.profile-dd__nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 4px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid var(--pd-line);
}
.nav-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 10px 10px;
  border-radius: 11px;
  color: var(--pd-ink);
  transition: background 0.15s ease;
}
.nav-row:hover {
  background: rgba(255, 255, 255, 0.85);
}
.nav-row__icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: rgba(15, 23, 42, 0.04);
  color: var(--pd-muted);
  flex-shrink: 0;
}
.nav-row__label {
  flex: 1;
  text-align: left;
  font-size: 13px;
  font-weight: 700;
}
.nav-row__chev {
  color: var(--pd-muted);
  opacity: 0.7;
}

.profile-dd__foot {
  padding: 10px 12px 12px;
  border-top: 1px solid var(--pd-line);
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.4), var(--pd-soft));
  flex: 0 0 auto;
}
.logout-btn {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid #9f1239;
  background: #be123c;
  color: #fff;
  border-radius: 11px;
  padding: 10px 12px;
  min-height: 40px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  transition:
    background 0.15s ease,
    transform 0.12s ease,
    box-shadow 0.15s ease;
}
.logout-btn:hover {
  background: #9f1239;
  box-shadow: 0 8px 18px -10px rgba(190, 18, 60, 0.55);
  transform: translateY(-0.5px);
}

/* —— Dark —— */
:global(html.jgi-dark .profile-dd){
  --pd-ink: #f8fafc;
  --pd-muted: #94a3b8;
  --pd-line: rgba(248, 250, 252, 0.1);
  --pd-surface: #0f172a;
  --pd-soft: #111827;
  --pd-soft2: #1e293b;
  --pd-accent: #2dd4bf;
  --pd-danger: #fb7185;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.05) inset,
    0 28px 56px -28px rgba(0, 0, 0, 0.75),
    0 0 0 1px rgba(255, 255, 255, 0.04);
}
:global(html.jgi-dark .profile-dd__hero),
:global(html.jgi-dark .profile-dd__topbar){
  background: linear-gradient(180deg, #134e4a 0%, #0f766e 100%);
}
:global(html.jgi-dark .profile-dd__session){
  background: rgba(15, 23, 42, 0.72);
  border-color: rgba(248, 250, 252, 0.08);
  box-shadow: none;
}
:global(html.jgi-dark .profile-stack){
  background: transparent;
  border: 0;
  box-shadow: none;
}
:global(html.jgi-dark .profile-stack--body){
  background: transparent;
}
:global(html.jgi-dark .profile-acc){
  background: #1e293b;
  border-color: #475569;
}
:global(html.jgi-dark .profile-acc.open){
  background: rgba(45, 212, 191, 0.1);
  border-color: #2dd4bf;
}
:global(html.jgi-dark .profile-acc__head:hover){
  background: rgba(255, 255, 255, 0.04);
}
:global(html.jgi-dark .profile-dd__fold-all){
  background: rgba(255, 255, 255, 0.16);
  border-color: rgba(255, 255, 255, 0.35);
  color: #fff;
}
:global(html.jgi-dark .profile-dd__fold-all:hover){
  background: rgba(255, 255, 255, 0.28);
}
:global(html.jgi-dark .profile-dd__facts){
  background: rgba(15, 23, 42, 0.72);
  border-color: rgba(248, 250, 252, 0.08);
}
:global(html.jgi-dark .fact-row){
  border-bottom-color: rgba(248, 250, 252, 0.08);
}
:global(html.jgi-dark .fact-row__value){
  color: #f1f5f9;
}
:global(html.jgi-dark .profile-dd__facts--demo .fact-row__value){
  color: #94a3b8;
}
:global(html.jgi-dark .fact-row__time){
  color: #7dd3fc;
  background: rgba(14, 165, 233, 0.16);
  border-color: rgba(56, 189, 248, 0.28);
}
:global(html.jgi-dark .pay-tile){
  background: rgba(15, 23, 42, 0.72);
  border-color: rgba(248, 250, 252, 0.08);
  box-shadow: none;
}
:global(html.jgi-dark .pay-tile__value){
  color: #f1f5f9;
}
:global(html.jgi-dark .profile-dd__pay--demo .pay-tile__value){
  color: #94a3b8;
}
:global(html.jgi-dark .pay-demo-badge){
  color: #5eead4;
  background: rgba(15, 23, 42, 0.9);
  border-color: rgba(45, 212, 191, 0.35);
}
:global(html.jgi-dark .profile-dd__session--urgent){
  background: rgba(67, 20, 7, 0.45);
  border-color: rgba(251, 146, 60, 0.35);
}
:global(html.jgi-dark .profile-dd__chip){
  background: color-mix(in srgb, var(--chip, #2dd4bf) 18%, #0f172a);
  border-color: color-mix(in srgb, var(--chip, #2dd4bf) 28%, transparent);
}
:global(html.jgi-dark .theme-btn:hover:not(.theme-btn--active)){
  background: rgba(255, 255, 255, 0.05);
}
:global(html.jgi-dark .nav-row:hover){
  background: rgba(255, 255, 255, 0.05);
}
:global(html.jgi-dark .nav-row__icon){
  background: rgba(248, 250, 252, 0.06);
}
:global(html.jgi-dark .logout-btn){
  background: #be123c;
  border-color: #fb7185;
  color: #fff;
}
:global(html.jgi-dark .logout-btn:hover){
  background: #9f1239;
}

/* —— Compact (HP / narrow) —— */
@media (max-width: 420px) {
  .profile-dd__name {
    font-size: 15px;
  }
  .profile-dd__avatar {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    font-size: 13px;
  }
  .profile-dd__body {
    padding: 10px 12px 2px;
  }
  .theme-btn {
    padding: 9px 6px;
    font-size: 11px;
  }
}

/* HP preview: viewport desktop, jadi pakai class (bukan @media) */
:global(html.jgi-device-hp .profile-dd),
:global(.jgi-device-studio--hp .profile-dd) {
  --pd-radius: 18px;
  max-height: 100%;
  border: 0;
  box-shadow: none;
}
:global(html.jgi-device-hp .profile-sheet-root),
:global(.jgi-device-studio--hp .profile-sheet-root) {
  padding: 0 !important;
  justify-content: stretch !important;
  box-sizing: border-box !important;
}
:global(html.jgi-device-hp .profile-sheet-root > .profile-dd--sheet),
:global(.jgi-device-studio--hp .profile-sheet-root > .profile-dd--sheet) {
  margin: 0 !important;
  flex: 1 1 auto !important;
  height: 100% !important;
  max-height: 100% !important;
  min-height: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
  border-radius: 20px 20px 0 0 !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
  box-shadow: 0 -12px 40px -12px rgba(15, 23, 42, 0.35) !important;
  background: #f1f5f9 !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-dd__topbar),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-dd__topbar) {
  display: flex !important;
  flex-direction: column !important;
  align-items: stretch !important;
  min-height: 0 !important;
  padding: 8px 12px 6px !important;
  border-bottom: 0 !important;
  background: linear-gradient(180deg, #0f766e 0%, #115e59 100%) !important;
  flex: 0 0 auto !important;
  gap: 2px !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-dd__top-actions),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-dd__top-actions) {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
  align-self: flex-end !important;
  gap: 6px !important;
  z-index: 1 !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-dd__top-actions .profile-dd__close),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-dd__top-actions .profile-dd__close),
:global(html.jgi-device-hp .profile-dd--sheet .profile-dd__top-actions .profile-dd__fold-all),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-dd__top-actions .profile-dd__fold-all) {
  position: static !important;
  top: auto !important;
  right: auto !important;
  width: 30px !important;
  height: 30px !important;
  padding: 0 !important;
  margin: 0 !important;
  border-radius: 9px !important;
  background: rgba(255, 255, 255, 0.16) !important;
  border: 1px solid rgba(255, 255, 255, 0.35) !important;
  color: #fff !important;
}
:global(html.jgi-device-hp .profile-dd__fold-all-txt),
:global(.jgi-device-studio--hp .profile-dd__fold-all-txt) {
  display: none !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-dd__hero),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-dd__hero) {
  padding: 4px 14px 14px !important;
  flex: 0 0 auto !important;
  text-align: left;
  background: linear-gradient(180deg, #115e59 0%, #0f766e 100%) !important;
  border-bottom: 0 !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-dd__hero-row),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-dd__hero-row) {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: flex-start !important;
  gap: 12px !important;
  padding: 12px !important;
  text-align: left !important;
  border-radius: 16px !important;
  background: rgba(255, 255, 255, 0.14) !important;
  border: 1px solid rgba(255, 255, 255, 0.28) !important;
  box-shadow: 0 8px 20px -12px rgba(15, 23, 42, 0.35) !important;
  backdrop-filter: none;
}
:global(html.jgi-device-hp .profile-dd__avatar),
:global(.jgi-device-studio--hp .profile-dd__avatar) {
  width: 48px !important;
  height: 48px !important;
  border-radius: 16px !important;
  font-size: 16px !important;
  margin: 0 !important;
  position: relative !important;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.28) inset,
    0 10px 20px -12px rgba(15, 118, 110, 0.75) !important;
}
:global(html.jgi-device-hp .profile-dd__name),
:global(.jgi-device-studio--hp .profile-dd__name) {
  font-size: 16px !important;
  letter-spacing: -0.025em;
  color: #fff !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-stack),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-stack) {
  margin: 0 !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  overflow: visible !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-stack--body),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-stack--body) {
  border-top: 0 !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-acc__head),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-acc__head) {
  padding: 12px 14px !important;
  min-height: 48px !important;
  column-gap: 10px !important;
  grid-template-columns: 34px minmax(0, 1fr) minmax(0, max-content) 28px !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-acc__ico),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-acc__ico) {
  width: 34px !important;
  height: 34px !important;
  border-radius: 11px !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-acc__title),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-acc__title) {
  font-size: 13.5px !important;
  font-weight: 750 !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-acc__peek),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-acc__peek) {
  font-size: 11px !important;
  font-weight: 800 !important;
  color: #115e59 !important;
  background: #ccfbf1 !important;
  border: 1px solid #5eead4 !important;
  padding: 3px 9px !important;
  border-radius: 999px !important;
  max-width: 100% !important;
  margin-left: 0 !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-acc__peek--empty),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-acc__peek--empty) {
  background: transparent !important;
  border: 0 !important;
  padding: 0 !important;
  width: 0 !important;
  max-width: 0 !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-dd__body),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-dd__body) {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  padding: 0 14px 12px !important;
  background: transparent !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 10px !important;
  overflow-x: hidden !important;
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch !important;
  overscroll-behavior: contain !important;
  touch-action: pan-y !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-dd__foot),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-dd__foot) {
  padding: 12px 14px 14px !important;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.72), #fff 40%) !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .logout-btn),
:global(.jgi-device-studio--hp .profile-dd--sheet .logout-btn) {
  min-height: 46px !important;
  padding: 11px 12px !important;
  font-size: 13.5px !important;
  border-radius: 14px !important;
}
:global(html.jgi-device-hp .profile-dd__hero-meta),
:global(.jgi-device-studio--hp .profile-dd__hero-meta) {
  display: flex !important;
  flex-direction: column !important;
  align-items: flex-start !important;
  text-align: left !important;
  min-width: 0;
  flex: 1 1 auto;
  width: auto;
  padding-right: 0 !important;
}
:global(html.jgi-device-hp .profile-dd__avatar),
:global(.jgi-device-studio--hp .profile-dd__avatar) {
  width: 48px !important;
  height: 48px !important;
  border-radius: 16px !important;
  font-size: 16px !important;
  margin: 0 !important;
}
:global(html.jgi-device-hp .profile-dd__kicker),
:global(.jgi-device-studio--hp .profile-dd__kicker) {
  font-size: 9px;
  letter-spacing: 0.12em;
  margin-bottom: 2px;
  text-align: left;
  color: #ccfbf1;
}
:global(html.jgi-device-hp .profile-dd__name),
:global(.jgi-device-studio--hp .profile-dd__name) {
  font-size: 16px !important;
  line-height: 1.2;
  text-align: left;
  color: #fff !important;
}
:global(html.jgi-device-hp .profile-dd__chip),
:global(.jgi-device-studio--hp .profile-dd__chip) {
  padding: 3px 9px !important;
  border-radius: 999px !important;
  font-size: 10px !important;
}
:global(html.jgi-device-hp .profile-dd__idline),
:global(.jgi-device-studio--hp .profile-dd__idline) {
  margin-top: 4px !important;
  gap: 5px !important;
  justify-content: flex-start !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-dd__top-actions .profile-dd__fold-all),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-dd__top-actions .profile-dd__fold-all) {
  position: static !important;
  top: auto !important;
  right: auto !important;
  width: 30px !important;
  height: 30px !important;
  padding: 0 !important;
  margin: 0 !important;
  border-radius: 9px !important;
}
:global(html.jgi-device-hp .profile-dd__fold-all-txt),
:global(.jgi-device-studio--hp .profile-dd__fold-all-txt) {
  display: none !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-stack),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-stack) {
  margin-top: 0 !important;
  border-radius: 16px !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-acc__head),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-acc__head) {
  padding: 12px 14px !important;
  min-height: 48px !important;
  column-gap: 10px !important;
  grid-template-columns: 34px minmax(0, 1fr) minmax(0, max-content) 28px !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-acc__ico),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-acc__ico) {
  width: 34px !important;
  height: 34px !important;
  border-radius: 11px !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-acc__title),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-acc__title) {
  font-size: 13.5px !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-acc__peek),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-acc__peek) {
  font-size: 11px !important;
  margin-left: 0 !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-acc__peek--empty),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-acc__peek--empty) {
  background: transparent !important;
  border: 0 !important;
  padding: 0 !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-acc__body),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-acc__body) {
  padding: 0 12px 12px !important;
}
:global(html.jgi-device-hp .profile-dd__session),
:global(.jgi-device-studio--hp .profile-dd__session) {
  margin-top: 0;
  padding: 7px 9px;
  gap: 8px;
  border-radius: 10px;
  text-align: left;
}
:global(html.jgi-device-hp .profile-dd__session-icon),
:global(.jgi-device-studio--hp .profile-dd__session-icon) {
  width: 24px;
  height: 24px;
  border-radius: 7px;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-dd__body),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-dd__body) {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  max-height: none !important;
  overflow-x: hidden !important;
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch !important;
  overscroll-behavior: contain !important;
  padding: 0 14px 12px !important;
  background: transparent !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 10px !important;
  touch-action: pan-y !important;
}
:global(html.jgi-device-hp .profile-dd__hero),
:global(.jgi-device-studio--hp .profile-dd__hero),
:global(html.jgi-device-hp .profile-dd__foot),
:global(.jgi-device-studio--hp .profile-dd__foot),
:global(html.jgi-device-hp .profile-dd__grab),
:global(.jgi-device-studio--hp .profile-dd__grab),
:global(html.jgi-device-hp .profile-dd__topbar),
:global(.jgi-device-studio--hp .profile-dd__topbar) {
  flex-grow: 0 !important;
  flex-shrink: 0 !important;
}
:global(html.jgi-device-hp .profile-dd__section),
:global(.jgi-device-studio--hp .profile-dd__section) {
  margin-bottom: 8px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
:global(html.jgi-device-hp .profile-dd__label),
:global(.jgi-device-studio--hp .profile-dd__label) {
  font-size: 9.5px;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
  gap: 4px;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-dd__foot),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-dd__foot) {
  padding: 12px 14px 14px !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .logout-btn),
:global(.jgi-device-studio--hp .profile-dd--sheet .logout-btn) {
  padding: 11px 12px !important;
  font-size: 13.5px !important;
  border-radius: 14px !important;
  min-height: 46px !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-dd__devices),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-dd__devices) {
  flex: 0 0 auto !important;
  margin: 0 !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-stack),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-stack) {
  flex: 0 0 auto !important;
  flex-shrink: 0 !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-acc__chev),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-acc__chev) {
  width: 28px !important;
  height: 28px !important;
  border-radius: 9px !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .profile-dd__fill),
:global(.jgi-device-studio--hp .profile-dd--sheet .profile-dd__fill) {
  flex: 0 0 auto !important;
  flex-grow: 0 !important;
  min-height: 0 !important;
  margin-top: auto !important;
  display: flex !important;
  align-items: center !important;
}

/* Tampilan / Bahasa / Mode — padat di sheet HP */
:global(html.jgi-device-hp .profile-dd--sheet .theme-switch),
:global(.jgi-device-studio--hp .profile-dd--sheet .theme-switch) {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
  gap: 3px !important;
  padding: 3px !important;
  border-radius: 10px !important;
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  overflow: hidden !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .theme-btn),
:global(.jgi-device-studio--hp .profile-dd--sheet .theme-btn) {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 4px !important;
  padding: 6px 4px !important;
  font-size: 11px !important;
  border-radius: 8px !important;
  min-height: 34px !important;
  min-width: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
  height: auto !important;
  box-sizing: border-box !important;
  overflow: hidden !important;
  white-space: nowrap !important;
  transform: none !important;
  margin: 0 !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .theme-btn .v-icon),
:global(.jgi-device-studio--hp .profile-dd--sheet .theme-btn .v-icon) {
  font-size: 15px !important;
  width: 15px !important;
  height: 15px !important;
  flex: 0 0 15px !important;
}

:global(html.jgi-device-hp .profile-dd--sheet .lang-switch--embedded),
:global(.jgi-device-studio--hp .profile-dd--sheet .lang-switch--embedded) {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
  width: 100% !important;
  max-width: 100% !important;
  gap: 3px !important;
  padding: 3px !important;
  border-radius: 10px !important;
  box-sizing: border-box !important;
  margin: 0 !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .lang-switch--embedded .lang-btn),
:global(.jgi-device-studio--hp .profile-dd--sheet .lang-switch--embedded .lang-btn) {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  min-width: 0 !important;
  min-height: 34px !important;
  padding: 6px 4px !important;
  font-size: 11px !important;
  gap: 5px !important;
  border-radius: 8px !important;
  box-sizing: border-box !important;
  margin: 0 !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .lang-flag),
:global(.jgi-device-studio--hp .profile-dd--sheet .lang-flag) {
  width: 16px !important;
  height: 11px !important;
  flex: 0 0 auto !important;
}

:global(html.jgi-device-hp .profile-dd--sheet .device-switch--embedded),
:global(.jgi-device-studio--hp .profile-dd--sheet .device-switch--embedded) {
  display: grid !important;
  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
  width: 100% !important;
  max-width: 100% !important;
  gap: 3px !important;
  padding: 3px !important;
  border-radius: 10px !important;
  box-sizing: border-box !important;
  margin: 0 !important;
  overflow: hidden !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .device-switch--embedded .device-btn),
:global(.jgi-device-studio--hp .profile-dd--sheet .device-switch--embedded .device-btn) {
  min-width: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
  height: 34px !important;
  min-height: 34px !important;
  max-height: 34px !important;
  padding: 4px 3px !important;
  gap: 4px !important;
  border-radius: 8px !important;
  box-sizing: border-box !important;
  overflow: hidden !important;
  transform: none !important;
  margin: 0 !important;
  align-self: stretch !important;
  flex-direction: row !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .device-btn__icon),
:global(.jgi-device-studio--hp .profile-dd--sheet .device-btn__icon) {
  width: 16px !important;
  height: 16px !important;
  border-radius: 5px !important;
  flex: 0 0 16px !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .device-btn__icon .v-icon),
:global(.jgi-device-studio--hp .profile-dd--sheet .device-btn__icon .v-icon) {
  font-size: 11px !important;
  width: 11px !important;
  height: 11px !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .device-btn__text),
:global(.jgi-device-studio--hp .profile-dd--sheet .device-btn__text) {
  display: flex !important;
  flex-direction: column !important;
  align-items: flex-start !important;
  justify-content: center !important;
  min-width: 0 !important;
  max-width: 100% !important;
  overflow: hidden !important;
  gap: 0 !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .device-btn__label),
:global(.jgi-device-studio--hp .profile-dd--sheet .device-btn__label) {
  font-size: 10px !important;
  line-height: 1.1 !important;
  max-width: 100% !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .device-btn__w),
:global(.jgi-device-studio--hp .profile-dd--sheet .device-btn__w) {
  display: none !important;
}
:global(html.jgi-device-hp .profile-dd--sheet .device-btn__w--on),
:global(.jgi-device-studio--hp .profile-dd--sheet .device-btn__w--on) {
  opacity: 0.9 !important;
}

:global(html.jgi-device-hp .profile-dd__nav),
:global(.jgi-device-studio--hp .profile-dd__nav) {
  margin: 0 0 4px;
  gap: 2px;
  padding: 3px;
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;
}
:global(html.jgi-device-hp .nav-row),
:global(.jgi-device-studio--hp .nav-row) {
  padding: 8px;
  gap: 8px;
  border-radius: 9px;
  min-height: 38px;
  width: 100%;
  box-sizing: border-box;
}
:global(html.jgi-device-hp .nav-row__icon),
:global(.jgi-device-studio--hp .nav-row__icon) {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  flex: 0 0 auto;
}
:global(html.jgi-device-hp .nav-row__label),
:global(.jgi-device-studio--hp .nav-row__label) {
  font-size: 12px;
  min-width: 0;
}
:global(html.jgi-device-hp .profile-dd__foot),
:global(.jgi-device-studio--hp .profile-dd__foot) {
  padding: 6px 12px 8px !important;
  flex: 0 0 auto !important;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  width: 100%;
  box-sizing: border-box;
}
:global(html.jgi-device-hp .logout-btn),
:global(.jgi-device-studio--hp .logout-btn) {
  padding: 8px 10px !important;
  font-size: 12px !important;
  border-radius: 10px !important;
  min-height: 36px !important;
  width: 100%;
  box-sizing: border-box;
}
:global(html.jgi-device-hp .profile-dd__locale-hint),
:global(.jgi-device-studio--hp .profile-dd__locale-hint) {
  margin-top: 6px;
  font-size: 9px;
}
</style>
